import { useRef, useState } from 'react'
import { conveyanceLabel } from '../lib/conveyance-label'
import { shortDate } from '../lib/site-dates'
import type { Confidence, SailingCardData } from './SailingCard'

// "Sailing schedule" — the lane's pulse as an editorial timeline dot-plot (Economist-style, not
// glossy bubbles). One MARKER per vessel on a horizontal date axis: x = departure, marker AREA =
// capacity (TEU), colour = carrier. Each marker sits on a thin stem off a baseline rule
// (lollipop), collision-packed into rows so nothing overlaps. Tap/hover a marker to read the
// vessel. The strip is wider than the viewport and pans — drag the slider or scroll. Wide gaps
// flag possible blank sailings.

const DAY = 86_400_000
const PX_PER_WEEK = 150
/** The line gave no ship. Not `TBN`: that is the API's word for a placeholder the line wrote. */
const NO_VESSEL = 'Vessel not given'

// Muted, editorial categorical palette (flat fills — no halos/specular gloss).
const PALETTE = [
  '#1f6f8b',
  '#3eaea0',
  '#e0533d',
  '#e8a33d',
  '#7d6b9e',
  '#5c8a5a',
  '#b5566b',
  '#3b6e8f',
  '#9c7a3c',
  '#6e8e84',
]

interface TLItem {
  vessel: string
  /** A ship's own name, so two records of it may be one dot; not TBN, a conveyance or nothing. */
  named: boolean
  etd: number
  teu: number | null
  carrier: string
  carrierName: string
  partnerCount: number
  partnerCodes: string[] // slot-share carriers, highest reliability first
  confLevel: Confidence['level']
  etdSpreadHours: number | null
  etaSpreadHours: number | null
  oldEtd: number | null // previous ETD if this sailing was rescheduled — drives the "moved" ghost
}

/** Which date the partners actually differ on, so the note never names one they agree about. */
function differsOn(etd: number | null, eta: number | null): string {
  const etdWide = (etd ?? 0) > 24
  const etaWide = (eta ?? 0) > 24
  if (etdWide && etaWide) return 'departure and arrival'
  return etaWide ? 'arrival' : 'departure'
}

export function LaneTimeline({ cards }: { cards: SailingCardData[] }) {
  // Hooks first — must run unconditionally before any early return.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<number | null>(null)
  const [pan, setPan] = useState(0)

  const items: TLItem[] = cards
    .filter((c) => c.published_departure)
    .map((c) => {
      const ranked = [...c.partners].sort(
        (a, b) => (b.reliability_pct ?? -1) - (a.reliability_pct ?? -1)
      )
      // last_move may be absent on an older API (staggered deploy) — guard to null.
      const oldMs = c.last_move?.old_departure
        ? new Date(c.last_move.old_departure).getTime()
        : null
      return {
        // `TBN` comes from the API only where the line wrote a placeholder; no name at all is not that
        // (SCHED-39), so it is said as what it is.
        // A sailing carried only by rail, truck or barge says which (Евгений 08.10).
        vessel: c.vessel_name?.trim() || conveyanceLabel(c.route_legs) || NO_VESSEL,
        named: Boolean(c.vessel_name?.trim()) && c.vessel_name?.trim() !== 'TBN',
        etd: new Date(c.published_departure as string).getTime(),
        teu: c.capacity_teu,
        carrier: ranked[0]?.carrier_code ?? '?',
        carrierName: ranked[0]?.carrier_name ?? ranked[0]?.carrier_code ?? '?',
        partnerCount: c.partner_count || c.partners.length,
        partnerCodes: ranked.map((p) => p.carrier_code),
        confLevel: c.confidence?.level ?? 'single_source',
        etdSpreadHours: c.confidence?.etd_spread_hours ?? null,
        etaSpreadHours: c.confidence?.eta_spread_hours ?? null,
        oldEtd: oldMs != null && !Number.isNaN(oldMs) ? oldMs : null,
      }
    })
    .filter((i) => !Number.isNaN(i.etd))
    .sort((a, b) => a.etd - b.etd)
  if (items.length < 3) return null

  // Merge records for the same physical vessel departing within 24h: slot-sharing partners that
  // arrive as separate API records collapse into one split-circle dot instead of two overlapping ones.
  const SAME_VESSEL_WINDOW = 24 * 3_600_000
  const merged: TLItem[] = []
  for (const it of items) {
    const m =
      // Two unnamed, two TBN or two rail sailings are not one ship.
      it.named
        ? merged.find(
            (x) => x.vessel === it.vessel && Math.abs(x.etd - it.etd) <= SAME_VESSEL_WINDOW
          )
        : undefined
    if (m) {
      const codes = [
        ...new Set([...m.partnerCodes, ...it.partnerCodes, it.carrier].filter(Boolean)),
      ]
      m.partnerCodes = codes
      m.partnerCount = codes.length
      if (it.teu != null && (m.teu == null || it.teu > m.teu)) m.teu = it.teu
      if (
        it.oldEtd != null &&
        (m.oldEtd == null || Math.abs(it.etd - it.oldEtd) > Math.abs(m.etd - (m.oldEtd ?? m.etd)))
      )
        m.oldEtd = it.oldEtd
    } else {
      merged.push({ ...it })
    }
  }

  const t0 = Math.min(merged[0]?.etd ?? Date.now(), Date.now())
  const t1 = merged[merged.length - 1]?.etd ?? t0 + 60 * DAY
  const span = Math.max(DAY, t1 - t0)

  // Colour over BOTH primaries and slot partners so every wedge of a shared dot gets its own hue
  // (and the legend lists them). Falls back to the primary carrier for single-carrier sailings.
  const carriers = [
    ...new Set(merged.flatMap((i) => (i.partnerCodes.length ? i.partnerCodes : [i.carrier]))),
  ]
  const color = (c: string) => PALETTE[Math.max(0, carriers.indexOf(c)) % PALETTE.length] as string

  // Wedge path: one of `segs` equal arcs of circle (cx,cy,r), starting at 12 o'clock, clockwise. Used
  // to split a slot-shared vessel's single dot into its carriers' colours — one dot, several lines.
  const wedgePath = (cx: number, cy: number, r: number, i: number, segs: number) => {
    const a0 = -Math.PI / 2 + (2 * Math.PI * i) / segs
    const a1 = -Math.PI / 2 + (2 * Math.PI * (i + 1)) / segs
    const x0 = cx + r * Math.cos(a0)
    const y0 = cy + r * Math.sin(a0)
    const x1 = cx + r * Math.cos(a1)
    const y1 = cy + r * Math.sin(a1)
    return `M${cx},${cy} L${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 0 1 ${x1.toFixed(2)},${y1.toFixed(2)} Z`
  }

  // Marker radius — lane-RELATIVE so each lane shows real contrast. sqrt curve → area tracks TEU.
  const teus = merged.map((i) => i.teu).filter((t): t is number => t != null && t > 0)
  const minT = teus.length ? Math.min(...teus) : 0
  const maxT = teus.length ? Math.max(...teus) : 1
  const R_MIN = 5
  const R_MAX = 22
  const radius = (teu: number | null) => {
    if (teu == null || maxT === minT) return (R_MIN + R_MAX) / 2 - 3
    const t = (teu - minT) / (maxT - minT)
    return R_MIN + Math.sqrt(t) * (R_MAX - R_MIN)
  }

  const padL = 28
  const padR = 28
  // Reserve the largest possible marker radius at both ends so an edge sailing's dot (e.g. the latest
  // ETD, hard right) is never clipped by the plot boundary — the time axis maps INSIDE this inset.
  const EDGE = R_MAX + 4
  const W = Math.max(880, padL + padR + 2 * EDGE + (span / (7 * DAY)) * PX_PER_WEEK)
  const x = (t: number) => padL + EDGE + ((t - t0) / span) * (W - padL - padR - 2 * EDGE)

  // Beeswarm: for each item (L→R), find the lowest cy above the baseline that doesn't
  // collide with any already-placed dot — Euclidean distance, no fixed grid.
  const GAP = 3 // minimum gap between dot edges (px)
  const STEM = 18 // baseline → nearest dot centre
  const TOP_PAD = 12 // breathing room above the topmost dot
  type BPlaced = { cx: number; relCy: number; r: number }
  const placedSoFar: BPlaced[] = []
  const swarmed = merged.map((it) => {
    const cx = x(it.etd)
    const r = radius(it.teu)
    let relCy = -(STEM + r) // negative = above baseline; start as close as possible
    for (let i = 0; i < 600; i++) {
      if (
        placedSoFar.every((p) => {
          const dx = cx - p.cx
          const dy = relCy - p.relCy
          return dx * dx + dy * dy >= (r + p.r + GAP) ** 2
        })
      )
        break
      relCy -= 1
    }
    placedSoFar.push({ cx, relCy, r })
    return { ...it, cx, r, relCy }
  })
  const maxUp = swarmed.reduce((m, p) => Math.max(m, -p.relCy + p.r), STEM + R_MAX)
  const baseY = TOP_PAD + maxUp
  const H = baseY + 36 // room for tick marks + date labels below the baseline
  const placed = swarmed.map((p) => ({ ...p, cy: baseY + p.relCy }))

  const weeks: number[] = []
  const wk = new Date(t0)
  wk.setUTCHours(0, 0, 0, 0)
  wk.setUTCDate(wk.getUTCDate() - ((wk.getUTCDay() + 6) % 7))
  for (let t = wk.getTime(); t <= t1 + 7 * DAY; t += 7 * DAY) weeks.push(t)

  const gaps = merged
    .slice(1)
    .map((it, idx) => ({ a: merged[idx]?.etd ?? it.etd, b: it.etd }))
    .filter((g) => g.b - g.a > 10 * DAY)

  const fmt = (t: number) => shortDate(t)
  const totalTeu = merged.reduce((s, i) => s + (i.teu ?? 0), 0)
  const pans = W > 1000

  const onSlider = (v: number) => {
    setPan(v)
    const el = scrollRef.current
    if (el) el.scrollLeft = (v / 100) * (el.scrollWidth - el.clientWidth)
  }
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setPan(max > 0 ? (el.scrollLeft / max) * 100 : 0)
  }

  // Active-marker callout (works on touch — SVG <title> doesn't). Rendered last so it's on top.
  const act = active != null ? placed[active] : null
  // Soft-confidence note: 'low' = ≥2 carriers who disagree a lot; 'single_source' = only one carrier on
  // this sailing so far. Honest, logistics-native — never a fabricated ±hours figure.
  //
  // It must name the date they actually differ on. `low` is earned by the WORSE of the departure and
  // arrival spreads, so a card whose departures agree to the hour can be low on the arrival alone, and
  // this line used to tell that customer the carriers differed on departure.
  const softNote =
    act && (act.confLevel === 'low' || act.confLevel === 'single_source')
      ? act.partnerCount > 1
        ? `  ·  carriers differ on ${differsOn(act.etdSpreadHours, act.etaSpreadHours)}`
        : '  ·  early data — one carrier'
      : ''
  const labelTeu = act?.teu ? ` · ${act.teu.toLocaleString()} TEU` : ''
  const labelBook =
    act && act.partnerCount > 1 ? `  ·  book via ${act.partnerCodes.slice(0, 4).join(' · ')}` : ''
  // Moved: only flag a reschedule once it's a visible (≥12h) shift, so the ghost doesn't overlap the dot.
  const MOVE_MIN = 12 * 3_600_000
  const moved = act?.oldEtd != null && Math.abs(act.etd - act.oldEtd) >= MOVE_MIN
  const labelMove =
    moved && act?.oldEtd != null
      ? `  ·  moved from ${fmt(act.oldEtd)} (${Math.max(1, Math.round(Math.abs(act.etd - act.oldEtd) / DAY))}d ${act.etd > act.oldEtd ? 'later' : 'earlier'})`
      : ''
  const labelText = act
    ? `${act.vessel} · ${act.carrierName} · ${fmt(act.etd)}${labelTeu}${labelBook}${softNote}${labelMove}`
    : ''
  // Moved-ghost geometry: ghost sits at the old ETD; arrow runs ghost→dot (right = later, left = earlier).
  const moveDir = moved && act && act.oldEtd != null && act.etd > act.oldEtd ? 1 : -1
  const ghostX = moved && act && act.oldEtd != null ? x(act.oldEtd) : 0
  // Keep the callout inside the VISIBLE window, not just the SVG: the strip scrolls (1 SVG unit = 1px),
  // so a label centred on a dot near the viewport edge would otherwise be clipped by the card border.
  // Read the live scroll offset (re-evaluated on each scroll via `pan`) and clamp into that window.
  const view = scrollRef.current
  const viewLeft = view ? view.scrollLeft : 0
  const viewW = view ? view.clientWidth : W
  const labelW = Math.min(viewW - 12, W - 8, Math.max(120, labelText.length * 6.6 + 16))
  const labelX = act
    ? Math.max(viewLeft + 6, Math.min(viewLeft + viewW - labelW - 6, act.cx - labelW / 2))
    : 0
  const labelAbove = act ? act.cy - act.r - 30 > 6 : true
  const labelY = act ? (labelAbove ? act.cy - act.r - 30 : act.cy + act.r + 6) : 0

  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between flex-wrap gap-x-3 mb-1">
        <h2 className="font-display font-bold text-2xl">Sailing schedule</h2>
        <p className="text-sm text-[var(--text-secondary)] font-mono tabular-nums">
          {merged.length} sailings · {fmt(t0)} → {fmt(t1)}
        </p>
      </div>
      <p className="text-xs text-[var(--text-muted)] mb-3">
        Each dot = one sailing — size = capacity (TEU), colour = carrier, placed by departure. Tap a
        dot for its name. Wide gaps flag possible blank sailings.
      </p>
      <div className="bg-surface-0 border border-[var(--border-strong)] rounded-2xl pt-2 pb-3">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="overflow-x-auto px-2"
          style={{ scrollbarWidth: 'thin' }}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: decorative chart — keyboard users read the sailings table below; click only dismisses the callout */}
          <svg
            viewBox={`0 0 ${W} ${H}`}
            width={W}
            height={H}
            role="img"
            aria-label="Sailing schedule — sailings by capacity and departure date"
            onClick={() => setActive(null)}
          >
            {gaps.map((g) => (
              <rect
                key={`gap-${g.a}`}
                x={x(g.a)}
                y={12}
                width={Math.max(0, x(g.b) - x(g.a))}
                height={baseY - 12}
                fill="var(--warn)"
                opacity={0.05}
              />
            ))}
            {/* week ticks off the baseline */}
            {weeks.map((t) => (
              <g key={`wk-${t}`}>
                <line
                  x1={x(t)}
                  x2={x(t)}
                  y1={baseY}
                  y2={baseY + 5}
                  stroke="var(--border-strong)"
                  strokeWidth={1}
                />
                <text
                  x={x(t)}
                  y={baseY + 19}
                  fill="var(--text-muted)"
                  fontSize="11"
                  fontFamily="ui-monospace, monospace"
                  textAnchor="middle"
                >
                  {fmt(t)}
                </text>
              </g>
            ))}
            {/* baseline rule */}
            <line
              x1={padL}
              x2={W - padR}
              y1={baseY}
              y2={baseY}
              stroke="var(--border-strong)"
              strokeWidth={1}
            />
            {placed.map((p, idx) => {
              const c = color(p.carrier)
              const isActive = active === idx
              // Slot-shared vessels split the dot into wedges — top-3 carriers by reliability plus one
              // neutral "other" wedge, so a 4-line sailing reads as 3 hues + grey, not 4 slivers.
              // Below r≈7 the wedges would be mud, so keep a solid dot (partners live in the callout).
              const segs =
                p.partnerCount > 1 && p.r >= 7
                  ? p.partnerCount > 3
                    ? [
                        ...p.partnerCodes.slice(0, 3).map((code) => ({ code, fill: color(code) })),
                        { code: 'other', fill: 'var(--text-muted)' },
                      ]
                    : p.partnerCodes.slice(0, 3).map((code) => ({ code, fill: color(code) }))
                  : null
              const soft = p.confLevel === 'low' || p.confLevel === 'single_source'
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: decorative marker — the sailings table below is the keyboard-accessible path
                <g
                  key={`s-${p.etd}-${idx}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setActive((a) => (a === idx ? null : idx))
                  }}
                  onMouseEnter={() => setActive(idx)}
                  onMouseLeave={() => setActive((a) => (a === idx ? null : a))}
                  style={{ cursor: 'pointer' }}
                >
                  {/* stem (lollipop) */}
                  <line
                    x1={p.cx}
                    x2={p.cx}
                    y1={baseY}
                    y2={p.cy}
                    stroke={c}
                    strokeWidth={1}
                    opacity={0.45}
                  />
                  {/* marker fill: solid for a single carrier, carrier-coloured wedges when slot-shared */}
                  {segs ? (
                    segs.map((s, si) => (
                      <path
                        key={s.code}
                        d={wedgePath(p.cx, p.cy, p.r, si, segs.length)}
                        fill={s.fill}
                        fillOpacity={isActive ? 1 : 0.82}
                      />
                    ))
                  ) : (
                    <circle
                      cx={p.cx}
                      cy={p.cy}
                      r={p.r}
                      fill={c}
                      fillOpacity={isActive ? 1 : 0.82}
                    />
                  )}
                  {/* one outer hairline so the wedges read as a single dot, not a pie */}
                  <circle
                    cx={p.cx}
                    cy={p.cy}
                    r={p.r}
                    fill="none"
                    stroke={isActive ? 'var(--text-primary)' : 'rgba(255,255,255,0.18)'}
                    strokeWidth={isActive ? 1.5 : 1}
                  />
                  {/* soft confidence: dashed inner ring when carriers disagree, or a single source */}
                  {soft && p.r >= 9 && (
                    <circle
                      cx={p.cx}
                      cy={p.cy}
                      r={p.r - 2.5}
                      fill="none"
                      stroke="var(--text-muted)"
                      strokeWidth={1}
                      strokeDasharray="3,2"
                      opacity={0.35}
                    />
                  )}
                </g>
              )
            })}
            {/* moved: hollow ghost at the old ETD + a dashed arrow to the current dot (active only) */}
            {moved && act && (
              <g pointerEvents="none">
                <circle
                  cx={ghostX}
                  cy={act.cy}
                  r={act.r}
                  fill="none"
                  stroke="var(--border-strong)"
                  strokeWidth={1}
                  opacity={0.4}
                />
                <line
                  x1={ghostX + moveDir * act.r}
                  y1={act.cy}
                  x2={act.cx - moveDir * act.r}
                  y2={act.cy}
                  stroke="var(--text-muted)"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                  opacity={0.55}
                />
                <path
                  d={`M${act.cx - moveDir * act.r},${act.cy} l${-moveDir * 5},-3 l0,6 Z`}
                  fill="var(--text-muted)"
                  opacity={0.7}
                />
              </g>
            )}
            {/* active callout — on top, touch-friendly */}
            {act && (
              <g pointerEvents="none">
                <rect
                  x={labelX}
                  y={labelY}
                  width={labelW}
                  height={24}
                  rx={5}
                  fill="var(--surface-2)"
                  stroke="var(--border-strong)"
                  strokeWidth={1}
                />
                <text
                  x={labelX + 8}
                  y={labelY + 16}
                  fill="var(--text-primary)"
                  fontSize="12"
                  fontFamily="ui-monospace, monospace"
                >
                  {labelText}
                </text>
              </g>
            )}
          </svg>
        </div>

        {/* pan slider */}
        {pans && (
          <div className="mt-2 px-4">
            <input
              type="range"
              min={0}
              max={100}
              step={0.5}
              value={pan}
              onChange={(e) => onSlider(Number(e.target.value))}
              aria-label="Pan the timeline"
              className="lane-pan w-full"
            />
          </div>
        )}

        {/* legend (fixed, outside the scroll) */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-4">
          {carriers.slice(0, 10).map((c) => (
            <span
              key={c}
              className="flex items-center gap-1.5 text-[11px] font-mono text-[var(--text-secondary)]"
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: color(c) }}
              />
              {c}
            </span>
          ))}
          {totalTeu > 0 && (
            <span className="ml-auto text-[11px] font-mono text-[var(--text-muted)] tabular-nums">
              {totalTeu.toLocaleString()} TEU over window
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
